/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, ConfigProvider, Flex, Layout, Menu } from 'antd';
import React, { useState } from 'react';
import type { MenuProps } from 'antd';
import {
  LineChartOutlined,
  SettingOutlined,
  AuditOutlined,
  ProjectOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  TableOutlined,
  UserOutlined,
  PhoneOutlined,
  ZoomInOutlined,
  BarChartOutlined,
  BulbOutlined,
  CustomerServiceOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { getUserInfo } from '../reducers/User/actions';
import { useSelector } from 'react-redux';
import { getSelectedHover } from '../reducers/User/actions';
import posthog from 'posthog-js';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const SiderBox: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true);
  const navigate = useNavigate();
  const current = window.location.pathname.split('/')[1];
  const userInfo: any = useSelector(getUserInfo);
  const isAgent = userInfo?.role === 'Agent';
  const permissions = userInfo.permissions || [];
  const agentId = userInfo.id || 0;
  const addons = userInfo?.addons || {};
  const globalHover = useSelector(getSelectedHover);
  const hideEmailFunctionality =
    process.env.REACT_APP_HIDE_EMAIL_INSIGHTS_FUNCTIONALITY === 'true' || !addons?.email_insights;

  const agentRoleAccessiblePages = [
    'agent',
    'agent-all-calls',
    'leaderboard',
    'agent-dashboard',
    'performance-analysis',
    'trend-analysis',
    'areas-of-improvement',
    'supervisor-feedback',
    'audit-review',
    'agent-audit-review',
  ];

  const getItem = (
    label: React.ReactNode,
    key: string,
    icon?: React.ReactNode,
    children?: MenuItem[] | null,
    type?: 'group' | null,
    callback?: () => void,
  ): MenuItem => {
    if (!addons?.email_insights) {
      console.log('emails');
    }

    if (key === 'customers' && !addons?.customer_insights) {
      return null;
    }
    const permission = (permissions || [])
      .filter((permission: any) => permission.type === 'page')
      .find((permission: any) => (permission.url || '').includes(key));
    if (
      (userInfo.role === 'Agent' && !agentRoleAccessiblePages.includes(key)) ||
      (userInfo.role !== 'Agent' && agentRoleAccessiblePages.includes(key))
    ) {
      return null;
    }

    if (key === 'rca' && !userInfo?.addons?.rca) {
      return null;
    }

    if (children) {
      const childrenPermission = children
        .filter((ch: any) => ch !== null)
        .map((child: any) => {
          const childPermission = (permissions || [])
            .filter((permission: any) => permission.type === 'page')
            .find((permission: any) => (permission.url || '').includes(child.key));
          return !(childPermission && !childPermission.allow);
        });
      if (childrenPermission.includes(false) || childrenPermission.length === 0) {
        return null;
      }
      return {
        key,
        icon,
        children,
        label,
        type,
      } as MenuItem;
    }

    if (permission && !permission.allow) {
      return null;
    }

    return {
      key,
      icon,
      children,
      label,
      type,
      onClick: callback,
    } as MenuItem;
  };

  const agentMenuItems: MenuItem[] = [
    getItem('Calls', 'agent-all-calls', <PhoneOutlined style={{ fontSize: 16 }} />, [
      getItem(<Link to={`/agent-all-calls`}>All Calls</Link>, 'agent-all-calls', null, null, null, () =>
        navigate('/agent-all-calls'),
      ),
    ]),
    getItem(
      <Link to={`/agent/agent-insights/${agentId}`}>My Insights</Link>,
      'agent',
      <LineChartOutlined style={{ fontSize: 16 }} />,
      null,
      null,
      () => navigate(`/agent/agent-insights/${agentId}`),
    ),
    getItem(
      <Link to={`/leaderboard`}>Leaderboard</Link>,
      'leaderboard',
      <BarChartOutlined style={{ fontSize: 16 }} />,
      null,
      null,
      () => navigate(`/leaderboard`),
    ),
    getItem('Audits', 'agent-audit-review', <AuditOutlined style={{ fontSize: 16 }} />, [
      getItem(<Link to={`/audit-review`}>Audit Review</Link>, 'audit-review', null, null, null, () =>
        navigate('/audit-review'),
      ),
    ]),
  ];

  const items: MenuItem[] = [
    getItem('Calls', 'call-analytics', <PhoneOutlined style={{ fontSize: 16 }} />, [
      getItem(<Link to={`/call-insights`}>Call Insights</Link>, 'call-insights', null, null, null, () =>
        navigate('/call-insights'),
      ),
      getItem(<Link to={`/all-calls`}>All Calls</Link>, 'all-calls', null, null, null, () => navigate('/all-calls')),
      getItem(<Link to={`/audit-accuracy`}>Audit Accuracy</Link>, 'audit-accuracy', null, null, null, () =>
        navigate('/audit-accuracy'),
      ),
    ]),
    hideEmailFunctionality
      ? null
      : getItem('Emails', 'email-analytics', <MailOutlined style={{ fontSize: 16 }} />, [
          getItem(
            <Link to={`/email-chain-insights`}>Email Chain Insights</Link>,
            'email-chain-insights',
            null,
            null,
            null,
            () => navigate('/email-chain-insights'),
          ),
          getItem(<Link to={`/all-email-chains`}>All Email Chains</Link>, 'all-email-chains', null, null, null, () =>
            navigate('/all-email-chains'),
          ),
        ]),

    getItem('Agents', 'agent-analytics', <CustomerServiceOutlined style={{ fontSize: 16 }} />, [
      getItem(
        <Link to={`/aggregate-agent-insights`}>Agent Insights</Link>,
        'aggregate-agent-insights',
        null,
        null,
        null,
        () => navigate('/aggregate-agent-insights'),
      ),
      getItem(
        <Link
          to={`/all-agents`}
          onClick={() => {
            posthog.capture('navigate_to_all_agents', {
              page: window.location.pathname,
            });
          }}
        >
          All Agents
        </Link>,
        'all-agents',
        null,
        null,
        null,
        () => navigate('/all-agents'),
      ),
      getItem(<Link to={`/agent-coaching`}>Coaching</Link>, 'agent-coaching', null, null, null, () =>
        navigate('/agent-coaching'),
      ),

      getItem(<Link to={`/agent-leaderboard`}>Leaderboard</Link>, 'agent-leaderboard', null, null, null, () =>
        navigate('/agent-leaderboard'),
      ),
    ]),
    getItem('Customers', 'customers', <UserOutlined style={{ fontSize: 16 }} />, [
      getItem(<Link to={`/customer-insights`}>Customer Insights</Link>, 'customer-insights', null, null, null, () =>
        navigate('/customer-insights'),
      ),
      getItem(<Link to={`/all-customers`}>All Customers</Link>, 'all-customers', null, null, null, () =>
        navigate('/all-customers'),
      ),
      getItem(
        <Link to={`/customer-parameters`}>Configure Parameters</Link>,
        'customer-parameters',
        null,
        null,
        null,
        () => navigate('/customer-parameters'),
      ),
    ]),
    getItem('Audits', 'audit-configuration', <AuditOutlined style={{ fontSize: 16 }} />, [
      getItem(<Link to={`/dispute-review`}>Dispute Review</Link>, 'dispute-review', null, null, null, () =>
        navigate('/dispute-review'),
      ),
      getItem(<Link to={`/audit-templates`}>All Audit Checklists</Link>, 'audit-templates', null, null, null, () =>
        navigate('/audit-templates'),
      ),
      getItem(
        <Link to={`/calls-that-need-attention`}>Calls That Need Attention</Link>,
        'calls-that-need-attention',
        null,
        null,
        null,
        () => navigate('/calls-that-need-attention'),
      ),
    ]),
    getItem(
      <Link to={`/reports`}>Reports</Link>,
      'reports',
      <ProjectOutlined style={{ fontSize: 16 }} />,
      null,
      null,
      () => navigate('/reports'),
    ),
    getItem(
      <Link to={`/rca`}>RCA - Voice of Customer</Link>,
      'rca',
      <ZoomInOutlined style={{ fontSize: 16 }} />,
      null,
      null,
      () => navigate('/rca'),
    ),
  ].filter((item): item is MenuItem => item !== null);

  const bottomItems: MenuItem[] = [
    getItem(
      <Link to={`/playground`}>Playground</Link>,
      'playground',
      <BulbOutlined style={{ fontSize: 16 }} />,
      null,
      null,
      () => navigate('/playground'),
    ),
    getItem(
      <Link to={`/settings`}>Settings</Link>,
      'settings',
      <SettingOutlined style={{ fontSize: 16 }} />,
      null,
      null,
      () => navigate('/settings'),
    ),
    getItem('Billing & Logs', 'billing-logs', <TableOutlined style={{ fontSize: 16 }} />, [
      getItem(<Link to={`/billing`}>Billing</Link>, 'billing', null, null, null, () => navigate('/billing')),
      getItem(<Link to={`/audit-logs`}>Audit logs</Link>, 'audit-logs', null, null, null, () =>
        navigate('/audit-logs'),
      ),
      getItem(<Link to={`/unprocessed-calls`}>Unprocessed Calls</Link>, 'unprocessed-calls', null, null, null, () =>
        navigate('/unprocessed-calls'),
      ),
    ]),
  ];

  return (
    <>
      <ConfigProvider
        theme={{
          components: {
            Menu: {
              itemBg: '#F6F8FA',
              itemSelectedBg: globalHover?.selectedBg,
              itemSelectedColor: globalHover?.selectedColor,
            },
          },
        }}
      >
        <Sider
          collapsed={collapsed}
          width="17%"
          style={{ paddingTop: 24, color: '#fff', maxHeight: '100vh', overflowY: 'auto', backgroundColor: '#F6F8FA' }}
          theme="light"
        >
          <Flex vertical style={{ height: 'calc(100vh - 86px)' }} justify="space-between">
            <Menu selectedKeys={[current]} mode="inline" items={isAgent ? agentMenuItems : items} />
            <div>
              <Menu selectedKeys={[current]} mode="inline" items={bottomItems} />
              <div style={{ height: 40, paddingInline: 20, marginInline: 4 }}>
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  style={{
                    fontSize: '16px',
                  }}
                />
              </div>
            </div>
          </Flex>
        </Sider>
      </ConfigProvider>
    </>
  );
};

export default SiderBox;
